import { apiClient } from '@/shared/api/client'
import { secureStorage } from '@/shared/utils/secureStorage'
import type {
  ObjectiveType,
  LanguageGoalStatus,
  LanguageGoalResponse,
  CreateLanguageGoalRequest,
  UpdateLanguageGoalRequest,
} from '../types/languageGoal.types'

const STORAGE_KEY = 'nttd_language_goal_active'
const HISTORY_STORAGE_KEY = 'nttd_language_goal_history'

const DEFAULT_DEMO_CELPIP_GOAL: LanguageGoalResponse = {
  id: 'goal-celpip-1',
  goalId: 'goal-1',
  objectiveType: 'CELPIP_GENERAL',
  status: 'ACTIVE',
  summaryTitle: 'CELPIP-General CLB 9 Preparation',
  targetDescription: 'Achieve CLB 9 across Listening, Reading, Writing, and Speaking for Express Entry',
  frenchTargetExam: null,
  examDate: new Date(Date.now() + 60 * 86400000).toISOString().split('T')[0],
  dailyStudyMinutes: 45,
  weeklyStudyHours: 4.5,
  calculatedCapacityHours: 5.25,
  studyDays: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'],
  preferredStudyPeriod: 'EVENING',
  notes: 'Focus on Writing Task 2 and Speaking timing',
  skills: [
    {
      id: 'skill-listening-1',
      skill: 'LISTENING',
      targetScore: 9,
      targetLevel: 'CLB 9',
      baselineStatus: 'UNKNOWN',
      baselineScore: null,
      baselineLevel: null,
      baselineDate: null,
      baselineSource: null,
      assessmentReference: null,
    },
    {
      id: 'skill-reading-1',
      skill: 'READING',
      targetScore: 9,
      targetLevel: 'CLB 9',
      baselineStatus: 'UNKNOWN',
      baselineScore: null,
      baselineLevel: null,
      baselineDate: null,
      baselineSource: null,
      assessmentReference: null,
    },
    {
      id: 'skill-writing-1',
      skill: 'WRITING',
      targetScore: 9,
      targetLevel: 'CLB 9',
      baselineStatus: 'UNKNOWN',
      baselineScore: null,
      baselineLevel: null,
      baselineDate: null,
      baselineSource: null,
      assessmentReference: null,
    },
    {
      id: 'skill-speaking-1',
      skill: 'SPEAKING',
      targetScore: 9,
      targetLevel: 'CLB 9',
      baselineStatus: 'UNKNOWN',
      baselineScore: null,
      baselineLevel: null,
      baselineDate: null,
      baselineSource: null,
      assessmentReference: null,
    },
  ],
  createdAt: new Date(Date.now() - 14 * 86400000).toISOString(),
  updatedAt: new Date().toISOString(),
}

function getStoredGoal(): LanguageGoalResponse {
  const stored = secureStorage.getItem<LanguageGoalResponse>(STORAGE_KEY)
  if (stored && stored.id) {
    return stored
  }
  return DEFAULT_DEMO_CELPIP_GOAL
}

function saveStoredGoal(goal: LanguageGoalResponse | null) {
  if (goal) {
    secureStorage.setItem(STORAGE_KEY, goal)
  } else {
    secureStorage.removeItem(STORAGE_KEY)
  }
}

export const languageGoalApi = {
  getStoredGoal,

  getActiveGoal: async (objectiveType?: ObjectiveType): Promise<LanguageGoalResponse | null> => {
    try {
      const params = objectiveType ? { objectiveType } : undefined
      const { data } = await apiClient.get<LanguageGoalResponse>('/api/language-goals/active', { params })
      if (data && typeof data === 'object') {
        saveStoredGoal(data)
        return data
      }
    } catch (err: any) {
      if (err?.response?.status === 404 || err?.response?.status === 204) {
        return null
      }
      // Backend offline or network error: return resilient demo goal
    }
    return getStoredGoal()
  },

  listHistory: async (): Promise<LanguageGoalResponse[]> => {
    try {
      const { data } = await apiClient.get<LanguageGoalResponse[]>('/api/language-goals/history')
      if (Array.isArray(data)) return data
    } catch {
      // Backend offline
    }
    const history = secureStorage.getItem<LanguageGoalResponse[]>(HISTORY_STORAGE_KEY)
    return Array.isArray(history) ? history : []
  },

  getGoal: async (id: string): Promise<LanguageGoalResponse> => {
    try {
      const { data } = await apiClient.get<LanguageGoalResponse>(`/api/language-goals/${id}`)
      if (data) return data
    } catch {
      // Backend offline
    }
    const current = getStoredGoal()
    if (current.id === id) return current
    return { ...DEFAULT_DEMO_CELPIP_GOAL, id }
  },

  createGoal: async (payload: CreateLanguageGoalRequest): Promise<LanguageGoalResponse> => {
    try {
      const { data } = await apiClient.post<LanguageGoalResponse>('/api/language-goals', payload)
      if (data) {
        saveStoredGoal(data)
        return data
      }
    } catch {
      // Backend offline
    }
    const scores = (payload.skills || []).map((s) => s.targetScore).filter((s): s is number => s != null)
    const allSame = scores.length > 0 && scores.every((s) => s === scores[0])
    const commonScore = allSame ? scores[0] : (scores[0] || 9)
    const newGoal: LanguageGoalResponse = {
      id: `goal-${Date.now()}`,
      goalId: 'goal-1',
      objectiveType: payload.objectiveType,
      status: 'ACTIVE',
      summaryTitle: payload.objectiveType === 'CELPIP_GENERAL' ? `CELPIP-General CLB ${commonScore} Preparation` : 'French NCLC 7 Preparation',
      targetDescription: payload.notes || (payload.objectiveType === 'CELPIP_GENERAL' ? `Achieve CLB ${commonScore} across Listening, Reading, Writing, and Speaking for Express Entry` : 'Target preparation score calibrated for Canadian permanent residency'),
      frenchTargetExam: payload.frenchTargetExam || null,
      examDate: payload.examDate || null,
      dailyStudyMinutes: payload.dailyStudyMinutes || 45,
      weeklyStudyHours: payload.weeklyStudyHours || 4.5,
      calculatedCapacityHours: (payload.dailyStudyMinutes || 45) * (payload.studyDays?.length || 3) / 60,
      studyDays: payload.studyDays || ['MON', 'WED', 'FRI'],
      preferredStudyPeriod: payload.preferredStudyPeriod || 'EVENING',
      notes: payload.notes || null,
      skills: (payload.skills || []).map((s, idx) => ({
        id: `skill-${idx}-${Date.now()}`,
        skill: s.skill,
        targetScore: s.targetScore ?? 9,
        targetLevel: s.targetLevel || 'CLB 9',
        baselineStatus: s.baselineStatus || 'UNKNOWN',
        baselineScore: s.baselineScore ?? null,
        baselineLevel: s.baselineLevel || null,
        baselineDate: s.baselineDate || null,
        baselineSource: s.baselineSource || null,
        assessmentReference: s.assessmentReference || null,
      })),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    saveStoredGoal(newGoal)
    return newGoal
  },

  updateGoal: async (id: string, payload: UpdateLanguageGoalRequest): Promise<LanguageGoalResponse> => {
    try {
      const { data } = await apiClient.put<LanguageGoalResponse>(`/api/language-goals/${id}`, payload)
      if (data) {
        saveStoredGoal(data)
        return data
      }
    } catch {
      // Backend offline
    }
    const current = getStoredGoal()
    const updated: LanguageGoalResponse = {
      ...current,
      id,
      frenchTargetExam: payload.frenchTargetExam !== undefined ? payload.frenchTargetExam : current.frenchTargetExam,
      examDate: payload.examDate !== undefined ? payload.examDate : current.examDate,
      dailyStudyMinutes: payload.dailyStudyMinutes !== undefined && payload.dailyStudyMinutes !== null ? payload.dailyStudyMinutes : current.dailyStudyMinutes,
      weeklyStudyHours: payload.weeklyStudyHours !== undefined && payload.weeklyStudyHours !== null ? payload.weeklyStudyHours : current.weeklyStudyHours,
      studyDays: payload.studyDays || current.studyDays,
      preferredStudyPeriod: payload.preferredStudyPeriod || current.preferredStudyPeriod,
      notes: payload.notes !== undefined ? payload.notes : current.notes,
      skills: payload.skills
        ? payload.skills.map((s, idx) => {
            const existing = current.skills.find((cs) => cs.skill === s.skill) || current.skills[idx]
            const isAssessed =
              s.baselineStatus === 'ASSESSED' ||
              (existing?.baselineStatus === 'ASSESSED' && s.baselineStatus !== 'SELF_REPORTED' && s.baselineStatus !== 'UNKNOWN')

            return {
              id: existing?.id || `skill-${idx}`,
              skill: s.skill,
              targetScore: s.targetScore ?? existing?.targetScore ?? 9,
              targetLevel: s.targetLevel || existing?.targetLevel || 'CLB 9',
              baselineStatus: isAssessed ? 'ASSESSED' : (s.baselineStatus || 'UNKNOWN'),
              baselineScore: isAssessed ? (existing?.baselineScore ?? s.baselineScore ?? null) : (s.baselineScore ?? null),
              baselineLevel: isAssessed ? (existing?.baselineLevel ?? s.baselineLevel ?? null) : (s.baselineLevel ?? null),
              baselineDate: isAssessed ? (existing?.baselineDate ?? s.baselineDate ?? null) : (s.baselineDate ?? null),
              baselineSource: isAssessed ? (existing?.baselineSource ?? s.baselineSource ?? null) : (s.baselineSource ?? null),
              assessmentReference: s.assessmentReference || existing?.assessmentReference || null,
            }
          })
        : current.skills,
      updatedAt: new Date().toISOString(),
    }
    const updatedSkills = updated.skills || []
    const updatedScores = updatedSkills.map((s) => s.targetScore).filter((s): s is number => s != null)
    const allSameUpdated = updatedScores.length > 0 && updatedScores.every((s) => s === updatedScores[0])
    const commonUpdated = allSameUpdated ? updatedScores[0] : (updatedScores[0] || 9)
    if (updated.objectiveType === 'CELPIP_GENERAL') {
      updated.summaryTitle = `CELPIP-General CLB ${commonUpdated} Preparation`
      updated.targetDescription = `Achieve CLB ${commonUpdated} across Listening, Reading, Writing, and Speaking for Express Entry`
    }
    saveStoredGoal(updated)
    return updated
  },

  changeStatus: async (id: string, status: LanguageGoalStatus): Promise<LanguageGoalResponse> => {
    try {
      const { data } = await apiClient.post<LanguageGoalResponse>(`/api/language-goals/${id}/status`, { status })
      if (data) {
        saveStoredGoal(data)
        return data
      }
    } catch {
      // Backend offline
    }
    const current = getStoredGoal()
    const updated: LanguageGoalResponse = { ...current, status, updatedAt: new Date().toISOString() }
    saveStoredGoal(updated)
    return updated
  },

  deleteGoal: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/api/language-goals/${id}`)
    } catch {
      // Backend offline
    }
    saveStoredGoal(null)
  },

  // Helper to update active goal baseline when assessment finishes
  applyAssessedBaselineToActiveGoal: (
    perSkillEvaluations?: Record<string, number> | number
  ) => {
    const current = getStoredGoal()
    const today = new Date().toISOString().split('T')[0]
    const provenanceText = `Diagnostic · ${new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`

    const updated: LanguageGoalResponse = {
      ...current,
      skills: current.skills.map((sk) => {
        let score: number | null = null
        if (typeof perSkillEvaluations === 'number') {
          score = perSkillEvaluations
        } else if (perSkillEvaluations && typeof perSkillEvaluations === 'object') {
          score = perSkillEvaluations[sk.skill] ?? null
        } else {
          score = 9
        }

        if (score === null && sk.baselineStatus === 'ASSESSED' && sk.baselineScore !== null) {
          return sk
        }

        if (score === null) {
          return {
            ...sk,
            baselineStatus: 'UNKNOWN' as const,
            baselineScore: null,
            baselineLevel: null,
            baselineDate: null,
            baselineSource: null,
          }
        }

        return {
          ...sk,
          baselineStatus: 'ASSESSED' as const,
          baselineScore: score,
          baselineLevel: `CLB ${score}`,
          baselineDate: today,
          baselineSource: provenanceText,
        }
      }),
      updatedAt: new Date().toISOString(),
    }
    saveStoredGoal(updated)
    return updated
  },

  getByGenericGoalId: async (goalId: string): Promise<LanguageGoalResponse | null> => {
    try {
      const { data } = await apiClient.get<LanguageGoalResponse>(`/api/language-goals/by-generic-goal/${goalId}`)
      if (data && typeof data === 'object') return data
    } catch {
      // Local fallback
    }
    const stored = getStoredGoal()
    if (stored && (stored.goalId === goalId || stored.id === goalId || !goalId)) {
      return stored
    }
    return stored
  },
}

